import { PaymentWorkflowType } from '../../container/forms/types/api/graph.types';
import { WorkflowRegistry } from '../../../types/workflow.types';

/**
 * Enhanced workflow configuration interface
 */
export interface EnhancedWorkflowConfig extends WorkflowRegistry {
  // Enhanced configuration options
  enableAutoSave?: boolean;
  enableValidation?: boolean;
  enableProgressTracking?: boolean;
  enableAuditLogging?: boolean;

  // Custom error messages
  errorMessages?: {
    networkError?: string;
    validationError?: string;
    permissionError?: string;
    genericError?: string;
  };

  // Form behavior configuration
  formBehavior?: {
    autoSaveInterval?: number; // in milliseconds
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    showProgressIndicator?: boolean;
  };

  // UI configuration
  uiConfig?: {
    showHeader?: boolean;
    showProgress?: boolean;
    showSaveButton?: boolean;
    showCancelButton?: boolean;
    theme?: 'light' | 'dark' | 'auto';
  };
}

/**
 * Default configuration for all workflows
 */
export const DEFAULT_WORKFLOW_CONFIG: Partial<EnhancedWorkflowConfig> = {
  enableAutoSave: true,
  enableValidation: true,
  enableProgressTracking: true,
  enableAuditLogging: false,

  errorMessages: {
    networkError: 'Network error occurred. Please check your connection.',
    validationError: 'Please fix the form errors before submitting.',
    permissionError: 'You do not have permission to perform this action.',
    genericError: 'An unexpected error occurred. Please try again.',
  },

  formBehavior: {
    autoSaveInterval: 30000, // 30 seconds
    validateOnChange: true,
    validateOnBlur: true,
    showProgressIndicator: true,
  },

  uiConfig: {
    showHeader: true,
    showProgress: true,
    showSaveButton: true,
    showCancelButton: true,
    theme: 'auto',
  },
};

/**
 * Workflow-specific configurations
 */
export const WORKFLOW_SPECIFIC_CONFIGS: Record<
  string,
  Partial<EnhancedWorkflowConfig>
> = {
  'dubai-payment-approval': {
    enableAutoSave: true,
    enableProgressTracking: true,
    formBehavior: {
      autoSaveInterval: 15000, // 15 seconds for critical workflows
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: true,
    },
    uiConfig: {
      showHeader: true,
      showProgress: true,
      showSaveButton: true,
      showCancelButton: false, // Don't allow cancellation for approval workflows
      theme: 'light',
    },
  },

  'dubai-payment-from-office-acc': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: false, // Single section, no progress needed
    },
    uiConfig: {
      showHeader: true,
      showProgress: false,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },

  'dubai-transfer-requisition': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: false,
    },
    uiConfig: {
      showHeader: true,
      showProgress: false,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },

  'overtime-approval': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: true,
    },
    uiConfig: {
      showHeader: true,
      showProgress: true,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },
  'gift-hospitality-donation-uk': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: true,
    },
    uiConfig: {
      showHeader: true,
      showProgress: true,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },
  'gift-hospitality-donation-dubai': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: true,
    },
    uiConfig: {
      showHeader: true,
      showProgress: true,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },
  'gift-hospitality-donation-dublin': {
    enableAutoSave: true,
    enableValidation: true,
    formBehavior: {
      autoSaveInterval: 30000,
      validateOnChange: true,
      validateOnBlur: true,
      showProgressIndicator: true,
    },
    uiConfig: {
      showHeader: true,
      showProgress: true,
      showSaveButton: true,
      showCancelButton: true,
      theme: 'auto',
    },
  },
// PLOP_INJECT_WORKFLOW_SPECIFIC_CONFIG
};

/**
 * Get enhanced configuration for a specific workflow
 */
export const getEnhancedWorkflowConfig = (
  workflowKey: string,
  baseWorkflow: WorkflowRegistry,
): EnhancedWorkflowConfig => {
  const specificConfig = WORKFLOW_SPECIFIC_CONFIGS[workflowKey] || {};

  return {
    ...baseWorkflow,
    ...DEFAULT_WORKFLOW_CONFIG,
    ...specificConfig,
    // Ensure error messages are properly merged
    errorMessages: {
      ...DEFAULT_WORKFLOW_CONFIG.errorMessages,
      ...specificConfig.errorMessages,
    },
    // Ensure form behavior is properly merged
    formBehavior: {
      ...DEFAULT_WORKFLOW_CONFIG.formBehavior,
      ...specificConfig.formBehavior,
    },
    // Ensure UI config is properly merged
    uiConfig: {
      ...DEFAULT_WORKFLOW_CONFIG.uiConfig,
      ...specificConfig.uiConfig,
    },
  };
};

/**
 * Validate workflow configuration
 */
export const validateWorkflowConfig = (
  config: EnhancedWorkflowConfig,
): string[] => {
  const errors: string[] = [];

  if (!config.id) {
    errors.push('Workflow ID is required');
  }

  if (!config.name) {
    errors.push('Workflow name is required');
  }

  if (!config.workflowType) {
    errors.push('Workflow type is required');
  }

  if (
    config.formBehavior?.autoSaveInterval &&
    config.formBehavior.autoSaveInterval < 5000
  ) {
    errors.push('Auto-save interval should be at least 5 seconds');
  }

  return errors;
};

/**
 * Get workflow configuration by type
 */
export const getWorkflowConfigByType = (
  workflowType: PaymentWorkflowType,
  allConfigs: Record<string, EnhancedWorkflowConfig>,
): EnhancedWorkflowConfig | null => {
  return (
    Object.values(allConfigs).find(
      (config) => config.workflowType === workflowType,
    ) || null
  );
};
